import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { api } from "@/lib/api";

const BRAND_FALLBACK = {
  part1: "Bezpieczna", part2: "Przestrzeń",
  weight1: "700", weight2: "700",
  color1: "#000000", color2: "#4C5A49",
  size1: "36", size2: "36",
};

const NAV = [
  { href: "#o-mnie", label: "O mnie" },
  { href: "#oferta", label: "Oferta" },
  { href: "/blog", label: "Blog", route: true },
  { href: "#opinie", label: "Opinie" },
  { href: "#faq", label: "FAQ" },
  { href: "#kontakt", label: "Kontakt" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [brand, setBrand] = useState(BRAND_FALLBACK);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/content")
      .then(({ data }) => { if (data?.brand) setBrand({ ...BRAND_FALLBACK, ...data.brand }); })
      .catch(() => {});
  }, []);

  const isHome = location.pathname === "/";

  const handleAnchor = (href) => {
    setOpen(false);
    if (!href.startsWith("#")) return;
    if (!isHome) {
      window.location.href = `/${href}`;
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#F9F8F6]/85 backdrop-blur-md border-b border-[#E5E0D8]" : "bg-transparent"
      }`}
    >
      <div className="container-prose flex items-center justify-between h-20">
        <Link
          to="/" data-testid="logo-link"
          className="font-display tracking-tight leading-none"
          style={{ fontWeight: brand.weight1, color: brand.color1, fontSize: `${brand.size1}px` }}
        >
          {brand.part1}{" "}
          <span style={{ fontWeight: brand.weight2, color: brand.color2, fontSize: `${brand.size2}px` }}>
            {brand.part2}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((n) =>
            n.route ? (
              <Link
                key={n.label}
                to={n.href}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="text-sm text-[#2B2B2B] hover:text-[#8A9A86] transition-colors tracking-wide"
              >
                {n.label}
              </Link>
            ) : (
              <button
                key={n.label}
                onClick={() => handleAnchor(n.href)}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="text-sm text-[#2B2B2B] hover:text-[#8A9A86] transition-colors tracking-wide"
              >
                {n.label}
              </button>
            )
          )}
          <button
            data-testid="nav-cta-button"
            onClick={() => handleAnchor("#rezerwacja")}
            className="bg-[#2B2B2B] text-white rounded-full px-6 py-3 text-sm hover:bg-[#8A9A86] transition-colors duration-300"
          >
            Umów sesję
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-[#2B2B2B]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F9F8F6] border-t border-[#E5E0D8]">
          <div className="container-prose py-6 flex flex-col gap-4">
            {NAV.map((n) =>
              n.route ? (
                <Link
                  key={n.label}
                  to={n.href}
                  onClick={() => setOpen(false)}
                  className="text-[#2B2B2B] py-2"
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </Link>
              ) : (
                <button
                  key={n.label}
                  onClick={() => handleAnchor(n.href)}
                  className="text-left text-[#2B2B2B] py-2"
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </button>
              )
            )}
            <button
              onClick={() => handleAnchor("#rezerwacja")}
              className="bg-[#2B2B2B] text-white rounded-full px-6 py-3 text-sm mt-2"
              data-testid="mobile-nav-cta"
            >
              Umów sesję
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
